import { makeAutoObservable, runInAction } from 'mobx'
import { handleAxiosError } from '~/shared/lib'
import { CustomerFiltersType, CustomersLoadingType } from './types'
import {
  CustomerFormType,
  CustomerType,
} from '~/shared/api/services/customers/types'
import { CustomersService } from '~/shared/api/services/customers'
import { INITIAL_FORM_CUSTOMERS } from '../constants/initial-form-customers'

export class CustomersStore {
  customers: CustomerType[] = []

  loaders: CustomersLoadingType = {
    customers: false,
    loadMoreCustomers: false,
    customerById: false,
    updateCustomer: false,
  }

  customerForm: CustomerFormType = INITIAL_FORM_CUSTOMERS

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof CustomersLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof CustomersLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = (data: CustomerType) => {
    this.customerForm = { ...this.customerForm, ...data }
  }

  setFormCustomersData = (
    key: keyof CustomerType,
    value: CustomerType[keyof CustomerType],
  ) => {
    this.customerForm = { ...this.customerForm, [key]: value }
  }

  getCustomers = async (options?: {
    action?: 'loadMoreCustomers'
    filters?: CustomerFiltersType
  }) => {
    const { action, filters } = options || {}
    if (action === 'loadMoreCustomers' && this.requestSettings.isAllLoaded)
      return
    this.activeLoader(action || 'customers')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset:
          action !== 'loadMoreCustomers' ? 0 : this.requestSettings.offset,
        ...filters,
      }

      const { data } = await CustomersService.getCustomers({ params })

      if (action === 'loadMoreCustomers') {
        runInAction(() => {
          this.customers = [...this.customers, ...data]
        })
      } else {
        runInAction(() => {
          this.customers = data
        })
      }
      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreCustomers'
              ? this.requestSettings.offset + this.requestSettings.limit
              : 20,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'customers')
    }
  }

  getCustomerById = async (id: string) => {
    this.activeLoader('customerById')
    try {
      const { data } = await CustomersService.getCustomerById(id)

      runInAction(() => {
        this.customerForm = data
      })
    } finally {
      this.closeLoader('customerById')
    }
  }
}
